import { useCallback, useEffect, useState } from "react";
import { useApiRequestMutation } from "../../services/apiService";
import Swal from "sweetalert2";
import { GoPencil } from "react-icons/go";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import "./profile.css";
import ChangePasswordModal from "../ChangePassword/ChangePassword";

const EmployeeProfile = () => {
    const navigate = useNavigate();
    const [apiRequest] = useApiRequestMutation();

    const [employeeData, setEmployeeData] = useState(null);
    const [loginSessions, setLoginSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        sortField: "last_accessed",
        sortOrder: "DESC",
    });

    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [showPasswords, setShowPasswords] = useState(false);

    const [passwordForm, setPasswordForm] = useState({
        current_password: "",
        new_password: "",
        confirm_password: "",
    });

    // --------------------------------------------------
    // Helper functions
    // --------------------------------------------------

    const getInitials = (name = "") => {
        return name
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map((word) => word.charAt(0))
            .join("")
            .substring(0, 2)
            .toUpperCase();
    };

    const getPageNumbers = () => {
        const currentPage = pagination.page;
        const pages = [];

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }

            return pages;
        }

        pages.push(1);

        if (currentPage > 3) {
            pages.push("...");
        }

        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (currentPage < totalPages - 2) {
            pages.push("...");
        }

        pages.push(totalPages);

        return pages;
    };

    const titleCase = (value = "") => {
        return String(value)
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const formatDate = (value) => {
        if (!value) return "-";

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) return "-";

        return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    };

    const formatDateTime = (value) => {
        if (!value) return "-";

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) return "-";

        return date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    // --------------------------------------------------
    // Get employee profile
    // --------------------------------------------------

    const getEmployeeProfile = useCallback(async () => {
        setLoading(true);

        try {
            const res = await apiRequest({
                url: "/admin/getUserProfile",
                method: "POST",
            }).unwrap();

            if (res?.success) {
                setEmployeeData(res.data || {});
            } else {
                // Swal.fire({
                //     icon: "error",
                //     title: "Error",
                //     text: res?.message || "Unable to fetch employee profile.",
                // });
            }
        } catch (error) {
            // Swal.fire({
            //     icon: "error",
            //     title: "Error",
            //     text:
            //         error?.data?.message ||
            //         error?.message ||
            //         "Unable to fetch employee profile.",
            // });
        } finally {
            setLoading(false);
        }
    }, [apiRequest]);

    // --------------------------------------------------
    // Get employee login sessions
    // --------------------------------------------------

    const getLoginSessions = useCallback(async () => {
        setLoadingSessions(true);

        try {
            const res = await apiRequest({
                url: "/admin/getAdminLoginSessions",
                method: "POST",
                data: {
                    page: pagination.page,
                    limit: pagination.limit,
                    sortField: pagination.sortField,
                    sortOrder: pagination.sortOrder,
                },
            }).unwrap();

            if (res?.success) {
                const logs = res.data?.logs || [];
                const total = res.data?.pagination?.total ?? logs.length;

                setLoginSessions(logs);

                setPagination((prev) => ({
                    ...prev,
                    total,
                }));
            } else {
                setLoginSessions([]);

                setPagination((prev) => ({
                    ...prev,
                    total: 0,
                }));
            }
        } catch (error) {
            setLoginSessions([]);

            setPagination((prev) => ({
                ...prev,
                total: 0,
            }));

            console.error(
                "Fetch Login Sessions Error:",
                error?.data?.message || error?.message
            );
        } finally {
            setLoadingSessions(false);
        }
    }, [
        apiRequest,
        pagination.page,
        pagination.limit,
        pagination.sortField,
        pagination.sortOrder,
    ]);

    useEffect(() => {
        getEmployeeProfile();
    }, [getEmployeeProfile]);

    useEffect(() => {
        getLoginSessions();
    }, [getLoginSessions]);

    // --------------------------------------------------
    // Upload employee profile image
    // --------------------------------------------------

    const handleProfileImage = async (event) => {
        const file = event.target.files?.[0];

        if (!file || isUploading) return;

        const allowedTypes = ["image/jpeg", "image/png"];

        if (!allowedTypes.includes(file.type)) {
            event.target.value = "";

            Swal.fire({
                icon: "warning",
                title: "Invalid Image",
                text: "Only JPG, JPEG and PNG images are allowed.",
            });

            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            event.target.value = "";

            Swal.fire({
                icon: "warning",
                title: "File Too Large",
                text: "Image size should not exceed 2 MB.",
            });

            return;
        }

        const formData = new FormData();
        formData.append("profile_image", file);

        setIsUploading(true);

        Swal.fire({
            title: "Uploading...",
            text: "Please wait while your profile image is updated.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            const res = await apiRequest({
                url: "/admin/uploadProfileImage",
                method: "POST",
                data: formData,
            }).unwrap();

            Swal.close();

            if (res?.success) {
                setEmployeeData((prev) => ({
                    ...prev,
                    profile_image: res.data?.profile_image,
                }));

                // Update your application's auth/context store here if needed.

                await Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: res.message || "Profile image updated successfully.",
                    confirmButtonColor: "#0782D5",
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Upload Failed",
                    text: res?.message || "Unable to upload profile image.",
                });
            }
        } catch (error) {
            Swal.close();

            Swal.fire({
                icon: "error",
                title: "Upload Failed",
                text: error?.data?.message || "Unable to upload profile image.",
            });
        } finally {
            setIsUploading(false);
            event.target.value = "";
        }
    };

    // --------------------------------------------------
    // Change password modal
    // --------------------------------------------------

    const openChangePasswordModal = () => {
        setPasswordForm({
            current_password: "",
            new_password: "",
            confirm_password: "",
        });

        setShowPasswords(false);
        setShowPasswordModal(true);
    };

    const closeChangePasswordModal = () => {
        if (!savingPassword) {
            setShowPasswordModal(false);
        }
    };

    const handlePasswordInput = (event) => {
        const { name, value } = event.target;

        setPasswordForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChangePassword = async (event) => {
        event.preventDefault();

        const {
            current_password,
            new_password,
            confirm_password,
        } = passwordForm;

        if (!current_password || !new_password || !confirm_password) {
            Swal.fire({
                icon: "warning",
                title: "Required Fields",
                text: "Please fill in all password fields.",
            });

            return;
        }

        if (new_password.length < 8) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Password",
                text: "New password must contain at least 8 characters.",
            });

            return;
        }

        if (new_password !== confirm_password) {
            Swal.fire({
                icon: "warning",
                title: "Password Mismatch",
                text: "New password and confirm password do not match.",
            });

            return;
        }

        setSavingPassword(true);

        Swal.fire({
            title: "Updating Password...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            const res = await apiRequest({
                url: "/employee/changePassword",
                method: "POST",
                data: {
                    current_password,
                    new_password,
                },
            }).unwrap();

            Swal.close();

            if (res?.success) {
                setShowPasswordModal(false);

                setPasswordForm({
                    current_password: "",
                    new_password: "",
                    confirm_password: "",
                });

                await Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: res.message || "Password changed successfully.",
                    confirmButtonColor: "#0782D5",
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: res?.message || "Unable to change password.",
                });
            }
        } catch (error) {
            Swal.close();

            Swal.fire({
                icon: "error",
                title: "Error",
                text: error?.data?.message || "Unable to change password.",
            });
        } finally {
            setSavingPassword(false);
        }
    };

    // --------------------------------------------------
    // Sessions pagination and sorting
    // --------------------------------------------------


    const handlePageChange = (page) => {
        const maxPages = Math.max(
            1,
            Math.ceil(pagination.total / pagination.limit)
        );

        if (
            !Number.isInteger(page) ||
            page < 1 ||
            page > maxPages ||
            loadingSessions
        ) {
            return;
        }

        setPagination((prev) => ({
            ...prev,
            page,
        }));
    };

    const handleLimitChange = (event) => {
        const limit = Number(event.target.value);

        if (![5, 10, 15].includes(limit)) {
            return;
        }

        setPagination((prev) => ({
            ...prev,
            page: 1,
            limit,
        }));
    };


    const handleSort = (field) => {
        setPagination((prev) => ({
            ...prev,
            page: 1,
            sortField: field,
            sortOrder:
                prev.sortField === field && prev.sortOrder === "ASC"
                    ? "DESC"
                    : "ASC",
        }));
    };

    const getSortIcon = (field) => {
        if (pagination.sortField !== field) {
            return "bi-chevron-expand";
        }

        return pagination.sortOrder === "ASC"
            ? "bi-chevron-up"
            : "bi-chevron-down";
    };

    // --------------------------------------------------
    // Loading state
    // --------------------------------------------------

    if (loading) {
        return (
            <div className="text-center mt-5">
                <div
                    className="spinner-border text-primary"
                    role="status"
                />
                <p className="mt-2 text-muted">Loading employee profile...</p>
            </div>
        );
    }

    if (!employeeData) {
        return (
            <div className="main-content">
                <div className="alert alert-danger">
                    Employee profile could not be loaded.
                    <button
                        className="btn btn-outline-primary btn-sm ms-3"
                        onClick={getEmployeeProfile}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }


    const totalPages = Math.max(
        1,
        Math.ceil(pagination.total / pagination.limit)
    );

    const firstRecord =
        pagination.total === 0
            ? 0
            : (pagination.page - 1) * pagination.limit + 1;

    const lastRecord = Math.min(
        pagination.page * pagination.limit,
        pagination.total
    );


    // --------------------------------------------------
    // UI
    // --------------------------------------------------

    return (
        <div>
            {/* Page header */}
            <div className="page-header">
                <div className="page-header-left d-flex align-items-center">
                    <ul className="breadcrumb mb-0">
                        <li className="breadcrumb-item">My Account</li>
                        <li className="breadcrumb-item active">
                            Employee Profile
                        </li>
                    </ul>
                </div>
            </div>

            <div className="main-content ">
                <div className="row">
                    <div className="p-6">

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="d-flex align-items-center gap-3">

                                {/* Rounded Back Button */}
                                <div
                                    onClick={() => navigate(-1)}
                                    className="back-btn d-flex align-items-center justify-content-center"
                                >
                                    <IoArrowBack size={16} />
                                </div>

                                <div>
                                    <h3 className="h4 mb-1 drawer-header">User Profile</h3>
                                    <p className="mb-0 text-muted">View your user information and security settings.</p>
                                </div>


                            </div>
                        </div>

                        {/* Basic Information */}
                        <div className="card p-4 mb-4 border-0 shadow-sm">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="profile-section-title">
                                    Basic Information
                                </h5>

                                <span
                                    className={`badge rounded-3 px-3 py-2 ${String(employeeData.employee_status).toLowerCase() ===
                                        "active"
                                        ? "bg-success-subtle text-success"
                                        : "bg-secondary-subtle text-secondary"
                                        }`}
                                >
                                    ● {titleCase(employeeData.employee_status || "Unknown")}
                                </span>
                            </div>

                            <hr />

                            <div className="row align-items-center mb-4">
                                <div className="col-auto position-relative">
                                    {employeeData.profile_image ? (
                                        <img
                                            src={employeeData.profile_image}
                                            alt="Employee profile"
                                            className="rounded-circle profile-image"
                                            width="80"
                                            height="80"
                                        />
                                    ) : (
                                        <div className="profile-avatar">
                                            {getInitials(
                                                employeeData.employee_name ||
                                                `${employeeData.first_name || ""} ${employeeData.last_name || ""
                                                }`
                                            )}
                                        </div>
                                    )}

                                    {isUploading && (
                                        <div className="profile-image-loader">
                                            <span className="spinner-border text-light" />
                                        </div>
                                    )}


                                  

                                </div>

                                <div className="col-auto">
                                    <label
                                        className={`profile-edit-icon ${isUploading ? "disabled" : ""
                                            }`}
                                        title="Change profile image"
                                    >
                                        <GoPencil size={22} />

                                        <input
                                            type="file"
                                            hidden
                                            accept="image/jpeg,image/png"
                                            disabled={isUploading}
                                            onChange={handleProfileImage}
                                        />
                                    </label>
                                </div>

                                <div className="col-12 mt-2">
                                    <small className="text-muted">
                                        Editable. Max 2 MB (JPG, PNG, JPEG)
                                    </small>
                                </div>
                            </div>

                            <div className="row g-4">
                                <div className="col-md-6">
                                    <label className="profile-label">Flexz</label>
                                    <input
                                        className="form-control"
                                        value={employeeData.flexz_id || ""}
                                        readOnly
                                        disabled
                                    />
                                    <small className="text-muted">Non editable</small>
                                </div>

                                <div className="col-md-6">
                                    <label className="profile-label">Employee ID</label>
                                    <input
                                        className="form-control"
                                        value={employeeData.employee_id || ""}
                                        readOnly
                                        disabled
                                    />
                                    <small className="text-muted">Non editable</small>
                                </div>

                                <div className="col-md-6">
                                    <label className="profile-label">
                                        Employee First Name
                                    </label>
                                    <input
                                        className="form-control"
                                        value={employeeData.first_name || ""}
                                        readOnly
                                        disabled
                                    />
                                    <small className="text-muted">Non editable</small>
                                </div>

                                <div className="col-md-6">
                                    <label className="profile-label">
                                        Employee Last Name
                                    </label>
                                    <input
                                        className="form-control"
                                        value={employeeData.last_name || ""}
                                        readOnly
                                        disabled
                                    />
                                    <small className="text-muted">Non editable</small>
                                </div>

                                <div className="col-md-6">
                                    <label className="profile-label">Email ID</label>
                                    <input
                                        className="form-control"
                                        value={employeeData.email || ""}
                                        readOnly
                                        disabled
                                    />
                                    <small className="text-muted">Non editable</small>
                                </div>

                                <div className="col-md-6">
                                    <label className="profile-label">
                                        Date of Joining
                                    </label>
                                    <input
                                        className="form-control"
                                        value={formatDate(employeeData.date_of_joining)}
                                        readOnly
                                        disabled
                                    />
                                    <small className="text-muted">Non editable</small>
                                </div>
                            </div>
                        </div>


                        <div className="card p-4 mb-4 border-0 shadow-sm">
                            <h5 className="profile-section-title">
                                Role &amp; Access Configuration
                            </h5>

                            <hr />

                            <label className="profile-label mb-3">
                                Assigned Role
                            </label>

                            <div>
                                <span className="badge border rounded-3 bg-gray-100 px-3 py-2 text-dark d-inline-flex align-items-center gap-2 fs-6 fw-medium profile-role-badge">
                                    <span className="rounded-circle bg-primary d-inline-block profile-role-dot" />
                                    {titleCase(
                                        employeeData.role_displayname || "Unassigned"
                                    )}
                                </span>
                            </div>

                            <small className="text-muted d-block mt-2">
                                Non editable
                            </small>
                        </div>

                        {/* Security */}
                        <div className="card p-4 mb-4 border-0 shadow-sm">
                            <h5 className="profile-section-title">Security</h5>

                            <hr />

                            <div className="mb-3">
                                <span className="fw-semibold">Password</span>
                            </div>

                            <div className="mb-4">
                                <button
                                    type="button"
                                    className="btn btn-link p-0 text-decoration-none text-primary"
                                    onClick={() => setShowPasswordModal(true)}
                                >
                                    Change Password
                                </button>
                            </div>

                            <div>
                                <h6 className="fw-semibold">Sessions</h6>

                                <small className="text-muted">
                                    All of your active sessions are listed below.
                                </small>



                                <div className="table-responsive mt-3">
                                    <table className="table table-hover mb-0">
                                        <thead>
                                            <tr>
                                                <th>Location &amp; IP</th>
                                                <th>OS &amp; Browser</th>
                                                <th
                                                    className="profile-sortable"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => handleSort("last_accessed")}
                                                >
                                                    Last accessed
                                                    <i
                                                        className={`bi ${getSortIcon("last_accessed")} ms-2`}
                                                    />
                                                </th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {loadingSessions && (
                                                <tr>
                                                    <td colSpan="4" className="text-center">
                                                        Loading sessions...
                                                    </td>
                                                </tr>
                                            )}

                                            {!loadingSessions && loginSessions.length === 0 && (
                                                <tr>
                                                    <td colSpan="4" className="text-center">
                                                        No records found
                                                    </td>
                                                </tr>
                                            )}

                                            {!loadingSessions &&
                                                loginSessions.map((session, index) => {
                                                    const isActive =
                                                        String(session.status || "").toLowerCase() ===
                                                        "active";

                                                    return (
                                                        <tr
                                                            key={
                                                                session.id ||
                                                                session._id ||
                                                                `${session.ip_address}-${index}`
                                                            }
                                                        >
                                                            <td>
                                                                {session.location || "Unknown location"}
                                                                <br />
                                                                <small className="text-muted">
                                                                    IP - {session.ip_address || "-"}
                                                                </small>
                                                            </td>

                                                            <td>{session.os_browser || "-"}</td>

                                                            <td>
                                                                {formatDateTime(session.last_accessed)}
                                                            </td>

                                                            <td>
                                                                <span
                                                                    className={
                                                                        isActive
                                                                            ? "text-primary fw-semibold"
                                                                            : "text-secondary"
                                                                    }
                                                                >
                                                                    {titleCase(session.status || "Unknown")}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>

                                    {/* Pagination */}
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-2">

                                        {/* Rows per page */}
                                        <div className="d-flex align-items-center gap-1 small">
                                            <span>Show</span>

                                            <select
                                                id="sessionLimit"
                                                className="form-select form-select-sm page-size-select"
                                                value={pagination.limit}
                                                onChange={(e) =>
                                                    handleLimitChange(e)
                                                }
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={15}>15</option>
                                            </select>

                                            <span>entries</span>
                                        </div>

                                        {/* Entry count */}
                                        <div className="small text-muted">
                                            Showing {firstRecord} to {lastRecord} of{" "}
                                            {pagination.total} entries
                                        </div>

                                        {/* Numbered pagination */}
                                        <ul className="pagination mb-0">

                                            <li
                                                className={`page-item ${pagination.page === 1 || loadingSessions
                                                    ? "disabled"
                                                    : ""
                                                    }`}
                                            >
                                                <button
                                                    type="button"
                                                    className="page-link"
                                                    disabled={
                                                        pagination.page === 1 || loadingSessions
                                                    }
                                                    onClick={() =>
                                                        handlePageChange(pagination.page - 1)
                                                    }
                                                >
                                                    Prev
                                                </button>
                                            </li>

                                            {getPageNumbers().map((page, index) =>
                                                page === "..." ? (
                                                    <li
                                                        key={`ellipsis-${index}`}
                                                        className="page-item disabled"
                                                    >
                                                        <span className="page-link">...</span>
                                                    </li>
                                                ) : (
                                                    <li
                                                        key={page}
                                                        className={`page-item ${pagination.page === page ? "active" : ""
                                                            }`}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="page-link"
                                                            disabled={loadingSessions}
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    </li>
                                                )
                                            )}

                                            <li
                                                className={`page-item ${pagination.page >= totalPages || loadingSessions
                                                    ? "disabled"
                                                    : ""
                                                    }`}
                                            >
                                                <button
                                                    type="button"
                                                    className="page-link"
                                                    disabled={
                                                        pagination.page >= totalPages ||
                                                        loadingSessions
                                                    }
                                                    onClick={() =>
                                                        handlePageChange(pagination.page + 1)
                                                    }
                                                >
                                                    Next
                                                </button>
                                            </li>

                                        </ul>
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </div>
            <ChangePasswordModal
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                changePasswordUrl="/admin/changePassword"
                logoutUrl="/admin/logout"
            />

        </div>
    );
};

export default EmployeeProfile;